import { useState } from "react";
import { Sparkle, Copy, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PURPOSES = [
  { v: "empanelment", l: "Request Empanelment" },
  { v: "introduction", l: "Introduce Our Firm" },
  { v: "follow_up", l: "Follow Up" },
  { v: "proposal", l: "Send a Proposal" },
];
const TONES = ["professional", "warm", "concise", "persuasive"];
const EMPTY = { bank_name: "", contact_person: "", designation: "", purpose: "empanelment", tone: "professional", extra_context: "" };

export default function Outreach() {
  const [form, setForm] = useState(EMPTY);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const generate = async (e) => {
    e?.preventDefault();
    if (!form.bank_name) { toast.error("Enter a bank name"); return; }
    setLoading(true);
    setDraft("");
    try {
      const { data } = await api.post("/ai/outreach", form);
      setDraft(data.draft);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(draft); toast.success("Copied to clipboard"); };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">AI Assistant</div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">AI Outreach Drafting</h1>
        <p className="text-sm text-muted-foreground mt-1">Generate tailored emails to bank managers to win new empanelments.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border">
          <form onSubmit={generate} className="space-y-4" data-testid="outreach-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bank Name *</Label><Input required value={form.bank_name} onChange={set("bank_name")} placeholder="State Bank of India" data-testid="outreach-bank" /></div>
              <div className="space-y-1.5"><Label>Branch / Location</Label><Input value={form.designation} onChange={set("designation")} placeholder="Branch Manager, Pandri" data-testid="outreach-designation" /></div>
            </div>
            <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact_person} onChange={set("contact_person")} placeholder="Mr. Sharma" data-testid="outreach-contact" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Purpose</Label>
                <Select value={form.purpose} onValueChange={set("purpose")}><SelectTrigger data-testid="outreach-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Tone</Label>
                <Select value={form.tone} onValueChange={set("tone")}><SelectTrigger data-testid="outreach-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Extra Context (optional)</Label><Textarea value={form.extra_context} onChange={set("extra_context")} rows={3} placeholder="e.g. We already serve 5 branches of this bank in Bilaspur…" data-testid="outreach-context" /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="generate-btn">
              <Sparkle className="mr-1" weight="fill" size={16} /> {loading ? "Drafting…" : "Generate Email"}
            </Button>
          </form>
        </Card>

        <Card className="p-6 border-border bg-primary text-white relative min-h-[300px] noise-overlay">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-semibold">Draft</h3>
            {draft && (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8" onClick={generate} data-testid="regenerate-btn"><ArrowClockwise size={15} className="mr-1" /> Redo</Button>
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8" onClick={copy} data-testid="copy-btn"><Copy size={15} className="mr-1" /> Copy</Button>
              </div>
            )}
          </div>
          {loading && <div className="text-white/60 font-mono text-sm animate-pulse">Writing your email…</div>}
          {!loading && !draft && <div className="text-white/50 text-sm">Fill the form and generate a professional outreach email. It will appear here, ready to copy.</div>}
          {draft && <pre className="whitespace-pre-wrap font-sans text-sm text-white/90 leading-relaxed" data-testid="draft-output">{draft}</pre>}
        </Card>
      </div>
    </div>
  );
}
